import { resolveNotificationUrl } from './notification-url.js';

/**
 * The Firebase Cloud Messaging service worker — ONE template for every app
 * that receives Web Push (customer-pwa, dashboard, agent-pwa), plus the Vite
 * plugin that serves it in dev and emits it at build.
 *
 * Node-side module (imported by each app's `vite.config.ts`): no DOM, no React,
 * and no `vite` import — the plugin is typed structurally so this package does
 * not have to depend on Vite.
 *
 * ── Why a service worker of our own, at `/firebase-messaging-sw.js` ──────────
 * A service worker cannot read `import.meta.env`, so the Firebase config is
 * baked in when the file is rendered. Each app registers it itself (see
 * `createPushClient`) with the push-only scope FCM uses by default, so it never
 * controls a page — the app's workbox `sw.js` keeps doing that.
 *
 * ── What it does that FCM's own worker did not ──────────────────────────────
 *  1. Taps open the RIGHT place. The registering page passes its router base
 *     in the script URL (`?base=/sparkle/`), and every link goes through
 *     `resolveNotificationUrl` — see that file for the rules. A payload's
 *     `data.tenantSlug`, when the server sends one, wins over the base.
 *  2. A tap FOCUSES an open window of the app and asks it to route there
 *     (postMessage `kilat-push-navigate`), instead of stacking a second copy
 *     of the app; only with no window open does it open one.
 *  3. No duplicate banners. When the payload carries a `notification` block,
 *     the FCM SDK already displays it, so `onBackgroundMessage` only shows a
 *     notification for DATA-ONLY payloads.
 *
 * Our `notificationclick` listener is registered BEFORE `firebase.messaging()`
 * so it runs first and `stopImmediatePropagation()`s the SDK's own handler,
 * which would otherwise open the raw, un-prefixed link in a second window.
 */

export interface FirebaseWebConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly storageBucket: string;
  readonly messagingSenderId: string;
  readonly appId: string;
  readonly measurementId?: string;
}

export interface RenderFcmSwOptions {
  readonly config: FirebaseWebConfig;
  /** Notification icon, RELATIVE to the app base (e.g. `icons/v0/192.png`). */
  readonly iconPath: string;
  /** Monochrome status-bar badge, relative to the app base. */
  readonly badgePath: string;
  /**
   * First path segments that are this app's own routes. Links starting with
   * one of these are root-relative and get the tenant base prefixed. `null`
   * means "every root-relative link is an app route" (single-base apps).
   */
  readonly routeRoots: readonly string[] | null;
  /** Firebase JS SDK compat version to importScripts. */
  readonly sdkVersion?: string;
  /** Which app this worker belongs to — only used in the header comment. */
  readonly appName: string;
}

export const FCM_SW_PATH = '/firebase-messaging-sw.js';
/** Same scope FCM registers its default worker at: controls no real page. */
export const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';
export const PUSH_NAVIGATE_MESSAGE = 'kilat-push-navigate';

const DEFAULT_SDK_VERSION = '10.13.2';

export function renderFcmServiceWorker(opts: RenderFcmSwOptions): string {
  const sdk = opts.sdkVersion ?? DEFAULT_SDK_VERSION;
  return `/* eslint-disable */
/**
 * ${opts.appName} — Firebase Cloud Messaging service worker.
 * GENERATED by @kilat/ui/push (packages/ui/src/push/fcm-sw.ts). Do not edit
 * the emitted file; edit the template.
 */
var SW_PARAMS = new URL(self.location.href).searchParams;
var RAW_BASE = SW_PARAMS.get('base') || '/';
var BASE = /^\\/(?:[a-z0-9-]+\\/)?$/.test(RAW_BASE) ? RAW_BASE : '/';
var ROUTE_ROOTS = ${JSON.stringify(opts.routeRoots)};
var ICON_PATH = ${JSON.stringify(opts.iconPath)};
var BADGE_PATH = ${JSON.stringify(opts.badgePath)};
var NAVIGATE_MESSAGE = ${JSON.stringify(PUSH_NAVIGATE_MESSAGE)};
var resolveNotificationUrl = ${resolveNotificationUrl.toString()};

function baseFor(data) {
  var slug = data && typeof data.tenantSlug === 'string' ? data.tenantSlug : '';
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? '/' + slug + '/' : BASE;
}

function linkFromNotification(notification) {
  var data = (notification && notification.data) || {};
  // Shown by the FCM SDK: the original payload rides under FCM_MSG.
  var fcm = data.FCM_MSG;
  if (fcm) {
    var fcmData = fcm.data || {};
    return {
      link: fcmData.deepLink || (fcm.fcmOptions && fcm.fcmOptions.link) || '',
      base: baseFor(fcmData),
    };
  }
  // Shown by onBackgroundMessage below.
  return { link: data.deepLink || '', base: baseFor(data) };
}

self.addEventListener('notificationclick', function (event) {
  if (event.action) return;
  event.stopImmediatePropagation();
  event.notification.close();
  var target = linkFromNotification(event.notification);
  var url = resolveNotificationUrl(target.link, target.base, self.location.origin, ROUTE_ROOTS);
  event.waitUntil(
    self.clients.matchAll({ type: 'window', includeUncontrolled: true }).then(function (wins) {
      var exact = null;
      var sameApp = null;
      for (var i = 0; i < wins.length; i++) {
        var win = wins[i];
        if (win.url === url) exact = exact || win;
        try {
          if (new URL(win.url).pathname.indexOf(target.base) === 0) sameApp = sameApp || win;
        } catch (e) {}
      }
      if (exact && 'focus' in exact) return exact.focus();
      if (sameApp && 'focus' in sameApp) {
        sameApp.postMessage({ type: NAVIGATE_MESSAGE, url: url });
        return sameApp.focus();
      }
      return self.clients.openWindow(url);
    }),
  );
});

importScripts('https://www.gstatic.com/firebasejs/${sdk}/firebase-app-compat.js');
importScripts('https://www.gstatic.com/firebasejs/${sdk}/firebase-messaging-compat.js');

firebase.initializeApp(${JSON.stringify(opts.config)});
var messaging = firebase.messaging();

// Data-only payloads only. A payload WITH a notification block is displayed by
// the SDK itself; showing it here too is how one push became two banners.
messaging.onBackgroundMessage(function (payload) {
  if (payload && payload.notification) return;
  var data = (payload && payload.data) || {};
  if (!data.title) return;
  var base = baseFor(data);
  self.registration.showNotification(data.title, {
    body: data.body || '',
    icon: data.icon || base + ICON_PATH,
    badge: base + BADGE_PATH,
    data: data,
    // A newer status for the same ticket replaces the older banner.
    tag: data.ticketId || data.tag || undefined,
    renotify: Boolean(data.ticketId || data.tag),
  });
});
`;
}

/** Read the Firebase web config from VITE_* env; null (or throw) when incomplete. */
export function readFirebaseConfigFromEnv(
  env: Readonly<Record<string, string | undefined>>,
  opts: { readonly throwIfMissing?: boolean; readonly appName?: string } = {},
): FirebaseWebConfig | null {
  const required = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID',
  ] as const;
  const missing = required.filter((k) => !env[k]);
  if (missing.length > 0) {
    if (opts.throwIfMissing) {
      throw new Error(
        `[kilat:fcm-service-worker] ${opts.appName ?? 'app'} is missing Firebase env vars: ` +
          `${missing.join(', ')}. The push service worker cannot initialise without them.`,
      );
    }
    return null;
  }
  const measurementId = env['VITE_FIREBASE_MEASUREMENT_ID'];
  return {
    apiKey: env['VITE_FIREBASE_API_KEY'] ?? '',
    authDomain: env['VITE_FIREBASE_AUTH_DOMAIN'] ?? '',
    projectId: env['VITE_FIREBASE_PROJECT_ID'] ?? '',
    storageBucket: env['VITE_FIREBASE_STORAGE_BUCKET'] ?? '',
    messagingSenderId: env['VITE_FIREBASE_MESSAGING_SENDER_ID'] ?? '',
    appId: env['VITE_FIREBASE_APP_ID'] ?? '',
    ...(measurementId ? { measurementId } : {}),
  };
}

const PLACEHOLDER_CONFIG: FirebaseWebConfig = {
  apiKey: 'DEV_MISSING',
  authDomain: 'DEV_MISSING',
  projectId: 'DEV_MISSING',
  storageBucket: 'DEV_MISSING',
  messagingSenderId: 'DEV_MISSING',
  appId: 'DEV_MISSING',
};

// ── Vite plugin (structurally typed; this package does not depend on Vite) ──

interface DevRequest {
  readonly url?: string;
}
interface DevResponse {
  setHeader(name: string, value: string): void;
  end(body: string): void;
}
interface DevServer {
  readonly middlewares: {
    use(fn: (req: DevRequest, res: DevResponse, next: () => void) => void): void;
  };
  readonly config: { readonly logger: { warn(msg: string): void } };
}
interface EmitContext {
  emitFile(file: { type: 'asset'; fileName: string; source: string }): string;
}

export interface FcmServiceWorkerPluginOptions extends Omit<RenderFcmSwOptions, 'config'> {
  /**
   * Resolve VITE_* env for a mode. The app passes a closure over Vite's
   * `loadEnv` + `process.env`, so this module needs neither.
   */
  readonly readEnv: (mode: string) => Readonly<Record<string, string | undefined>>;
}

export interface FcmServiceWorkerPlugin {
  readonly name: string;
  config(userConfig: unknown, configEnv: { readonly mode: string }): void;
  configureServer(server: DevServer): void;
  generateBundle(this: EmitContext): void;
}

export function fcmServiceWorkerPlugin(
  opts: FcmServiceWorkerPluginOptions,
): FcmServiceWorkerPlugin {
  let env: Readonly<Record<string, string | undefined>> = {};
  const render = (config: FirebaseWebConfig) =>
    renderFcmServiceWorker({
      config,
      iconPath: opts.iconPath,
      badgePath: opts.badgePath,
      routeRoots: opts.routeRoots,
      appName: opts.appName,
      ...(opts.sdkVersion ? { sdkVersion: opts.sdkVersion } : {}),
    });

  return {
    name: 'kilat:fcm-service-worker',
    config(_userConfig, { mode }) {
      env = opts.readEnv(mode);
    },
    configureServer(server) {
      server.middlewares.use((req, res, next) => {
        if (!req.url || !req.url.startsWith(FCM_SW_PATH)) return next();
        const config = readFirebaseConfigFromEnv(env);
        if (!config) {
          server.config.logger.warn(
            `[kilat:fcm-service-worker] ${opts.appName}: serving the push worker with placeholder ` +
              'Firebase config — set VITE_FIREBASE_* in .env.local to receive pushes in dev.',
          );
        }
        res.setHeader('Content-Type', 'application/javascript; charset=utf-8');
        // Always revalidate so a template change is picked up on reload.
        res.setHeader('Cache-Control', 'no-store, max-age=0');
        res.end(render(config ?? PLACEHOLDER_CONFIG));
      });
    },
    generateBundle() {
      // Hard-fail: a worker with placeholder config silently breaks every push.
      const config = readFirebaseConfigFromEnv(env, {
        throwIfMissing: true,
        appName: opts.appName,
      });
      if (!config) throw new Error('[kilat:fcm-service-worker] config missing after guard');
      this.emitFile({
        type: 'asset',
        fileName: 'firebase-messaging-sw.js',
        source: render(config),
      });
    },
  };
}
